import {createSelector} from 'reselect';

import Editor from 'Editor';
import {isStructuredTableNotePart} from 'notebook/notes/groups/util/assertIsStructuredTableNotePart';
import {getTablePlugin} from 'notebook/notes/plugins/getTablePlugin';
import {EvaluatedCell, EvaluatedTable, FullTableMap} from 'notebook/tables/excess/Evaluator';
import {Table} from 'notebook/tables/TableConstants';
import {isPlainTable, isStructuredTable} from 'notebook/TypePredicates';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';

export function getFirstTableNotePartIndex(note: Note): number | null {
  return note.noteParts.findIndex((part) => isPlainTable(part) || isStructuredTable(part.item!)) ?? null;
}

export function getTableNotePartIndex(note: Note, tableName: string): number | null {
  const index = note.noteParts.findIndex(
    (part) =>
      (part.item && isStructuredTable(part.item) && part.item.name === tableName) ||
      (isPlainTable(part) && part?.table?.name === tableName)
  );
  return index > -1 ? index : null;
}

export function getTableNotePart(note: Note, tableName: string): NotePart | null {
  const notePartIndex = getTableNotePartIndex(note, tableName);
  if (notePartIndex == null || notePartIndex < 0) {
    return null;
  }
  // assert(notePartIndex != null, `Note part index not found for ${tableName}`);
  return note.noteParts[notePartIndex];
}

export function getAllTableNoteParts(note: Note): Array<NotePart> {
  const noteParts = note.noteParts;
  return noteParts.filter((notePart) => isPlainTable(notePart) || isStructuredTableNotePart(notePart));
}

export function getTableFromNotePart(notePart: NotePart): {table: Table} {
  const plugin = getTablePlugin(notePart);
  return plugin.getContent.call({} as Editor, notePart);
}

export function getTableMapFromNote(note: Note): FullTableMap {
  const tableNoteParts = getAllTableNoteParts(note);
  return tableNoteParts.reduce((acc, notePart) => {
    const {table} = getTableFromNotePart(notePart);
    acc[table.name] = table;
    return acc;
  }, {} as FullTableMap);
}

// TODO(BNCH-126510): This is just the start of building a hydrated table selector
// This does not compute errors yet, it just uses the plugin to generate the table object.
export function createHydratedTableSelector(tableName: string): (note: Note) => Table | null {
  return createSelector([(note: Note) => getTableNotePart(note, tableName)], (notePart) => {
    if (!notePart) {
      return null;
    }
    const plugin = getTablePlugin(notePart);
    return (plugin.getContent.call({} as Editor, notePart) as {table: Table}).table;
  });
}

export function getEvaluatedTable(tableName: string): (note: Note) => EvaluatedTable | null {
  return (note: Note) => note.noteRuntimeSnapshot?.evaluationSnapshot?.[tableName] ?? null;
}

export function getEvaluatedRow(
  tableName: string,
  rowIndex: number
): (note: Note) => Array<EvaluatedCell> | null {
  return (note: Note) => getEvaluatedTable(tableName)(note)?.[rowIndex] ?? null;
}

export function getEvaluatedCell(
  tableName: string,
  rowIndex: number,
  columnIndex: number
): (note: Note) => EvaluatedCell | null {
  return (note: Note) => getEvaluatedRow(tableName, rowIndex)(note)?.[columnIndex] ?? null;
}
